/**
 * TooltipHost — one delegated tooltip for the whole app.
 *
 * Replaces the native `title` attribute, whose ~0.5–1s hover delay, grey
 * box and lack of keyboard support are all hardcoded in the browser and
 * adjustable from neither CSS nor JS. The only fix is to render our own.
 *
 * Usage: put `data-tip="…"` on ANY element instead of `title="…"`.
 * Newlines in the value are preserved (the bubble is `white-space: pre-line`).
 *
 * Mounted ONCE in App. It listens at the document level rather than
 * wrapping each trigger in a component, which means:
 *   - converting a call site is a pure attribute rename — no JSX
 *     restructuring, so table/flex layouts can't be disturbed;
 *   - the bubble renders in a portal on <body> with position:fixed, so it
 *     is never clipped by an `overflow:hidden` table cell or card.
 *
 * Behaviour: opens after a short dwell on hover, INSTANTLY on keyboard
 * focus (focus is deliberate; pointer travel is not), closes on pointer-out,
 * blur, Escape, scroll or resize. Sets `aria-describedby` on the live
 * trigger so screen readers announce the bubble as its description.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../styles/tooltip.css';

/** Hover dwell before opening. The native tooltip is ~1000ms — being far
 *  below that is the entire point of this component. */
const OPEN_DELAY_MS = 90;
/** Gap between trigger and bubble. */
const OFFSET_PX = 8;
/** Minimum distance the bubble keeps from the viewport edge. */
const MARGIN_PX = 8;
const BUBBLE_ID = 'app-tooltip';

interface TipState {
  text: string;
  rect: DOMRect;
}

export function TooltipHost(): React.ReactPortal | null {
  const [tip, setTip] = useState<TipState | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  // The element currently described by the bubble — tracked so we can
  // remove the aria-describedby we added to it.
  const describedRef = useRef<HTMLElement | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    clearTimer();
    if (describedRef.current) {
      describedRef.current.removeAttribute('aria-describedby');
      describedRef.current = null;
    }
    setTip(null);
  }, [clearTimer]);

  const open = useCallback((el: HTMLElement, immediate: boolean) => {
    const text = el.getAttribute('data-tip');
    if (!text) return;
    const commit = (): void => {
      el.setAttribute('aria-describedby', BUBBLE_ID);
      describedRef.current = el;
      setTip({ text, rect: el.getBoundingClientRect() });
    };
    clearTimer();
    if (immediate) {
      commit();
      return;
    }
    timerRef.current = window.setTimeout(commit, OPEN_DELAY_MS);
  }, [clearTimer]);

  useEffect(() => {
    const triggerOf = (t: EventTarget | null): HTMLElement | null =>
      t instanceof Element
        ? (t.closest('[data-tip]') as HTMLElement | null)
        : null;

    const onOver = (e: PointerEvent | MouseEvent): void => {
      const el = triggerOf(e.target);
      if (!el) {
        // Left the trigger (or never was on one).
        if (describedRef.current || timerRef.current !== null) hide();
        return;
      }
      if (el === describedRef.current) return; // already showing for this one
      hide();
      open(el, false);
    };
    const onFocusIn = (e: FocusEvent): void => {
      const el = triggerOf(e.target);
      if (el) open(el, true);
      else hide();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') hide();
    };

    document.addEventListener('mouseover', onOver);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', hide);
    document.addEventListener('keydown', onKey);
    // A tooltip anchored to a stale rect is worse than none.
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      clearTimer();
    };
  }, [open, hide, clearTimer]);

  /* Position after the bubble has been measured: prefer above, flip below
     when there isn't room, then clamp horizontally into the viewport.
     Written straight to the node (not through state) so it lands in the
     same frame and the bubble never visibly jumps. */
  useEffect(() => {
    const el = bubbleRef.current;
    if (!tip || !el) return;
    const box = el.getBoundingClientRect();
    const { rect } = tip;

    const needed = box.height + OFFSET_PX + MARGIN_PX;
    const below = rect.top < needed && window.innerHeight - rect.bottom >= needed;
    const top = below ? rect.bottom + OFFSET_PX : rect.top - box.height - OFFSET_PX;

    let left = rect.left + rect.width / 2 - box.width / 2;
    left = Math.max(MARGIN_PX, Math.min(left, window.innerWidth - box.width - MARGIN_PX));

    el.style.top = `${Math.max(MARGIN_PX, top)}px`;
    el.style.left = `${left}px`;
    el.style.visibility = 'visible';
  }, [tip]);

  if (!tip) return null;

  return createPortal(
    <div
      ref={bubbleRef}
      id={BUBBLE_ID}
      role="tooltip"
      className="tt-bubble"
      // Hidden for the first frame — before the effect above measures it,
      // its position is meaningless and it would flash in the wrong place.
      style={{ visibility: 'hidden' }}
    >
      {tip.text}
    </div>,
    document.body,
  );
}

export default TooltipHost;
